// Rich text for note blocks.
//
// contentEditable + document.execCommand. execCommand is deprecated, but no
// standard replacement exists for formatting inside contentEditable, it works
// in every current browser, and it costs nothing — which matters against the
// spec's minimal-dependency brief. If this proves limiting, a real editor
// (TipTap) slots in behind the same block interface without touching drag,
// resize, persistence, or linearisation.

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'BR', 'DIV', 'P', 'SPAN',
  'UL', 'OL', 'LI', 'A', 'H1', 'H2', 'H3', 'FONT', 'CODE', 'BLOCKQUOTE',
])

/**
 * Strip anything that could execute. The threat model here is modest — one
 * user, behind Cloudflare Access — but pasted content comes from anywhere, and
 * this HTML is rendered back with innerHTML.
 */
export function sanitizeHtml(html: string): string {
  const tpl = document.createElement('template')
  tpl.innerHTML = html

  for (const el of Array.from(tpl.content.querySelectorAll('*'))) {
    if (!ALLOWED_TAGS.has(el.tagName)) {
      // Keep the text, drop the element.
      el.replaceWith(...Array.from(el.childNodes))
      continue
    }
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase()
      const value = attr.value
      const isSafeStyleOrFormat =
        name === 'style' || name === 'color' || name === 'face' || name === 'size'
      const isSafeLink = name === 'href' && !/^\s*javascript:/i.test(value)
      if (name.startsWith('on') || !(isSafeStyleOrFormat || isSafeLink)) {
        el.removeAttribute(attr.name)
      }
    }
    if (el.tagName === 'A') {
      el.setAttribute('target', '_blank')
      el.setAttribute('rel', 'noopener noreferrer')
    }
  }
  return tpl.innerHTML
}

/** Plain text from HTML, for the client-side preview of reading order. */
export function htmlToText(html: string): string {
  const tpl = document.createElement('template')
  tpl.innerHTML = html
  return (tpl.content.textContent ?? '').trim()
}

/** Escape plain text into HTML — used to migrate pre-rich-text blocks. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
}

export const TEXT_COLORS = [
  { label: 'Default', value: '#1f2933' },
  { label: 'Slate', value: '#64748b' },
  { label: 'Red', value: '#b91c1c' },
  { label: 'Amber', value: '#b45309' },
  { label: 'Green', value: '#15803d' },
  { label: 'Blue', value: '#1d4ed8' },
  { label: 'Violet', value: '#6d28d9' },
]

export const FILL_COLORS = [
  { label: 'None', value: 'transparent' },
  { label: 'Slate', value: '#f1f5f9' },
  { label: 'Red', value: '#fee2e2' },
  { label: 'Amber', value: '#fef3c7' },
  { label: 'Green', value: '#dcfce7' },
  { label: 'Blue', value: '#dbeafe' },
  { label: 'Violet', value: '#ede9fe' },
]
